import React, { useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasSuperPrivileges } from "@/lib/accountRole";
import { toast } from "@/lib/toast";
import { Crosshair } from "@/components/Crosshair";
import { Loader2, ShieldAlert, Trash2, Search, Download, ShieldCheck, Database, AlertTriangle } from "lucide-react";

const CONFIRM_PHRASE = "PURGE TEST ACCOUNTS";

function downloadJson(obj, filename) {
  try {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) {
    /* download is best-effort; the archive is also saved server-side */
  }
}

function ConfirmModal({ scan, onClose, onConfirm, busy }) {
  const [typed, setTyped] = useState("");
  const ok = typed.trim().toUpperCase() === CONFIRM_PHRASE;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="tap-confirm" onClick={onClose}>
      <div className="bg-surface border border-blaze/60 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b border-sniper">
          <AlertTriangle className="w-5 h-5 text-blaze" />
          <h3 className="font-heading text-lg font-bold uppercase text-foreground">Hard-delete test accounts</h3>
        </div>
        <div className="px-5 py-4 space-y-3 font-body text-sm text-secondary-foreground">
          <p>
            This permanently deletes <span className="text-foreground font-bold" data-testid="tap-confirm-count">{scan.candidate_count}</span> test
            account{scan.candidate_count === 1 ? "" : "s"} and <span className="text-foreground font-bold">{scan.associated_doc_total}</span> owned record{scan.associated_doc_total === 1 ? "" : "s"} across the database.
          </p>
          <ul className="font-mono text-[11px] uppercase text-muted-foreground space-y-1">
            <li className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-olive-bright" /> {scan.exempt_count} QA fixtures kept</li>
            <li className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-olive-bright" /> {scan.demo_untouched_count} demo accounts untouched</li>
            <li className="flex items-center gap-2"><Download className="w-3.5 h-3.5 text-volt" /> A JSON archive downloads before deletion</li>
          </ul>
          <p className="text-blaze font-mono text-[11px] uppercase">This can't be undone. Type the phrase to confirm.</p>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            data-testid="tap-confirm-input"
            className="w-full bg-input border border-sniper focus:border-blaze outline-none px-3 py-2.5 text-foreground font-mono text-sm uppercase"
          />
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-sniper">
          <button onClick={onClose} data-testid="tap-confirm-cancel" className="border border-sniper hover:border-white/40 text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-4 py-2 transition-colors">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={!ok || busy}
            data-testid="tap-confirm-purge"
            className="inline-flex items-center gap-2 border border-blaze bg-blaze/15 text-blaze hover:bg-blaze/25 disabled:opacity-40 disabled:cursor-not-allowed font-mono uppercase text-xs px-4 py-2 transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Archive &amp; purge
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminTestData() {
  const { user } = useAuth();
  const isSuper = hasSuperPrivileges(user?.role);
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showExempt, setShowExempt] = useState(false);

  const runScan = useCallback(async () => {
    setScanning(true);
    setResult(null);
    try {
      const { data } = await api.get("/admin/test-accounts/scan");
      setScan(data);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Scan failed.");
    } finally {
      setScanning(false);
    }
  }, []);

  const doPurge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/test-accounts/purge", {
        confirm: true,
        expected_user_count: scan.candidate_count,
      });
      if (data.archive) {
        downloadJson(data.archive, `opticswap-test-account-purge-${data.run_id || "run"}.json`);
      }
      setResult(data);
      setConfirmOpen(false);
      toast.success(`Purged ${data.deleted_user_count} account(s) · archive downloaded.`);
      runScan();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error("The candidate list changed since your scan — re-scanning.");
        runScan();
      } else {
        toast.error(formatError(e?.response?.data?.detail) || "Purge failed.");
      }
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-4 py-6 text-foreground" data-testid="admin-test-data">
      <div className="flex items-center gap-3">
        <Database className="w-7 h-7 text-olive-bright" strokeWidth={1.6} />
        <div>
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight" data-testid="admin-test-data-title">Test-account purge</h2>
          <p className="text-xs font-mono uppercase text-muted-foreground mt-1">Hard-remove QA/testing junk accounts &amp; the data they created</p>
        </div>
      </div>
      <Crosshair className="my-5" />

      {!isSuper && (
        <div className="border border-amber-400/40 bg-amber-400/10 p-4 flex items-center gap-2 font-mono text-[11px] uppercase text-amber-300 mb-5" data-testid="tap-super-note">
          <ShieldAlert className="w-4 h-4" /> Super-admin only. You can scan (read-only) but not purge.
        </div>
      )}

      <div className="border border-sniper bg-surface p-5 space-y-3">
        <p className="font-body text-sm text-secondary-foreground">
          Scans every <span className="font-mono text-foreground">is_test</span> account. It archives (JSON download) then permanently deletes them
          and everything they own. <span className="text-foreground">Demo accounts are never touched</span>, and the documented QA fixtures the test suite depends on are kept.
        </p>
        <button onClick={runScan} disabled={scanning} data-testid="tap-scan-btn"
          className="inline-flex items-center gap-2 border border-olive hover:bg-olive/10 text-foreground font-mono uppercase text-xs px-4 py-2.5 transition-colors disabled:opacity-50">
          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {scan ? "Re-scan" : "Scan test accounts"}
        </button>
      </div>

      {scan && (
        <div className="mt-6 space-y-4" data-testid="tap-scan-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="border border-blaze/50 bg-surface p-4">
              <p className="font-mono text-[10px] uppercase text-muted-foreground">Will delete</p>
              <p className="font-heading text-3xl font-bold text-blaze tabular-nums" data-testid="tap-candidate-count">{scan.candidate_count}</p>
              <p className="font-mono text-[10px] uppercase text-muted-foreground">accounts</p>
            </div>
            <div className="border border-sniper bg-surface p-4">
              <p className="font-mono text-[10px] uppercase text-muted-foreground">Owned records</p>
              <p className="font-heading text-3xl font-bold text-foreground tabular-nums" data-testid="tap-assoc-total">{scan.associated_doc_total}</p>
              <p className="font-mono text-[10px] uppercase text-muted-foreground">across collections</p>
            </div>
            <div className="border border-olive/50 bg-surface p-4">
              <p className="font-mono text-[10px] uppercase text-muted-foreground">Kept (fixtures)</p>
              <p className="font-heading text-3xl font-bold text-olive-bright tabular-nums" data-testid="tap-exempt-count">{scan.exempt_count}</p>
              <p className="font-mono text-[10px] uppercase text-muted-foreground">exempt</p>
            </div>
            <div className="border border-sniper bg-surface p-4">
              <p className="font-mono text-[10px] uppercase text-muted-foreground">Demo (untouched)</p>
              <p className="font-heading text-3xl font-bold text-foreground tabular-nums" data-testid="tap-demo-count">{scan.demo_untouched_count}</p>
              <p className="font-mono text-[10px] uppercase text-muted-foreground">never deleted</p>
            </div>
          </div>

          {Object.keys(scan.associated_doc_counts || {}).length > 0 && (
            <div className="border border-sniper bg-surface p-4">
              <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Records to delete, by collection</p>
              <div className="flex flex-wrap gap-2" data-testid="tap-assoc-breakdown">
                {Object.entries(scan.associated_doc_counts).map(([k, v]) => (
                  <span key={k} className="font-mono text-[10px] uppercase border border-sniper px-2 py-1 text-secondary-foreground">
                    {k} <span className="text-foreground tabular-nums">{v}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* candidate list */}
          <div className="border border-sniper bg-surface">
            <div className="px-4 py-2.5 border-b border-sniper font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
              Accounts to delete ({scan.candidate_count})
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-sniper/50" data-testid="tap-candidate-list">
              {(scan.candidates || []).map((c) => (
                <div key={c.user_id} className="px-4 py-2 flex items-center justify-between gap-3 font-mono text-[11px]" data-testid={`tap-candidate-${c.username}`}>
                  <span className="text-foreground truncate">@{c.username}</span>
                  <span className="text-muted-foreground truncate flex-1 text-right">{c.email}</span>
                </div>
              ))}
              {scan.candidate_count === 0 && <p className="px-4 py-6 text-center font-mono text-[11px] uppercase text-muted-foreground" data-testid="tap-candidates-empty">No test accounts to purge — all clear.</p>}
            </div>
          </div>

          <button onClick={() => setShowExempt((v) => !v)} data-testid="tap-toggle-exempt" className="font-mono text-[10px] uppercase text-olive-bright hover:underline">
            {showExempt ? "Hide" : "Show"} kept fixtures ({scan.exempt_count})
          </button>
          {showExempt && (
            <div className="border border-olive/40 bg-surface max-h-60 overflow-y-auto divide-y divide-sniper/50" data-testid="tap-exempt-list">
              {(scan.exempt || []).map((c) => (
                <div key={c.user_id} className="px-4 py-2 flex items-center justify-between gap-3 font-mono text-[11px]">
                  <span className="text-foreground truncate">@{c.username}</span>
                  <span className="text-muted-foreground truncate">{c.email}</span>
                  <span className="text-olive-bright shrink-0">{c.exempt_reason}</span>
                </div>
              ))}
            </div>
          )}

          {isSuper && scan.candidate_count > 0 && (
            <button onClick={() => setConfirmOpen(true)} data-testid="tap-purge-btn"
              className="inline-flex items-center gap-2 border border-blaze bg-blaze/10 text-blaze hover:bg-blaze/20 font-mono uppercase text-xs px-4 py-2.5 transition-colors">
              <Trash2 className="w-4 h-4" /> Archive &amp; purge {scan.candidate_count} account{scan.candidate_count === 1 ? "" : "s"}
            </button>
          )}
        </div>
      )}

      {result && (
        <div className="mt-6 border border-olive/50 bg-olive/5 p-4" data-testid="tap-result">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase text-olive-bright">
            <ShieldCheck className="w-4 h-4" /> Purge complete · run {result.run_id || "—"}
          </div>
          <p className="mt-2 font-body text-sm text-secondary-foreground" data-testid="tap-result-summary">
            Deleted <span className="text-foreground font-bold">{result.deleted_user_count}</span> account(s) and{" "}
            <span className="text-foreground font-bold">{(result.deleted_total || 0) - (result.deleted_user_count || 0)}</span> owned record(s).
            The archive JSON was downloaded and saved server-side.
          </p>
        </div>
      )}

      {confirmOpen && <ConfirmModal scan={scan} busy={busy} onClose={() => setConfirmOpen(false)} onConfirm={doPurge} />}
    </div>
  );
}
